import type { AxleListing } from '@contracts/axle/AxleListing';
import type { VehicleAxlesList } from '@src/axles/domain/VehicleAxlesList';
import { generateNEmptyAxles } from '@src/axles/domain/VehicleAxlesList';
import type { BaseInputType, InputType } from '@src/orders/FormEntries';
import type { TrailerInformationFormEntries } from '@src/orders/PermitFormSections/haulingEquipment/TrailerInformationSection';
import { AxleFormName } from '@src/permitField/AxleInformationFields';
import { TrailerIdField } from '@src/permitField/TrailerInformationFields';
import { newTrailerKey } from '@src/trailer/TrailerSelector';
import type { TFunction } from 'i18next';

export type VehicleType = 'TRAILER' | 'TRACTOR';

export type AxleSummaryGroup = {
  name: string;
  groupType: VehicleType;
  vehicleId: string;
  axles: AxleListing[];
};

export class AxleSummary {
  private readonly totalCount: number;

  constructor(readonly axleGroup: AxleSummaryGroup[]) {
    this.totalCount = this.axleGroup.reduce(
      (previousValue, currentValue) =>
        previousValue + currentValue.axles.length,
      0,
    );
  }

  static fromFormAndPersistedValues(
    trailerFormValues: InputType | undefined,
    tractorFormValues: {
      id: InputType | undefined;
      axles: InputType | undefined;
    },
    persistedVehicleAxlesList: VehicleAxlesList,
  ): AxleSummary {
    const trailerAxleGroups: AxleSummaryGroup[] = trailerFormValues
      ? (trailerFormValues as TrailerInformationFormEntries[]).reduce(
          (acc, trailerInformation, i) => {
            if (!trailerInformation) {
              return acc;
            }
            const trailerAxleCount = trailerInformation[AxleFormName]
              ? (trailerInformation[AxleFormName] as BaseInputType[]).length
              : 0;
            const trailerId =
              (trailerInformation[TrailerIdField.fieldName] as string) ??
              newTrailerKey;
            const summary = this.assembleGroupSummary(
              trailerId,
              trailerAxleCount,
              persistedVehicleAxlesList,
              (i + 1).toString(),
              'TRAILER',
            );

            if (summary) {
              acc.push(summary);
              return acc;
            }
            return acc;
          },
          [] as AxleSummaryGroup[],
        )
      : [];

    const tractorAxleCount = Array.isArray(tractorFormValues.axles)
      ? tractorFormValues.axles.length
      : 0;
    const tractorId = AxleSummary.toStringOrUndefined(tractorFormValues.id);
    const tractorAxlesSummary = this.assembleGroupSummary(
      tractorId,
      tractorAxleCount,
      persistedVehicleAxlesList,
      '1',
      'TRACTOR',
    );
    if (tractorAxlesSummary) {
      return new AxleSummary([tractorAxlesSummary, ...trailerAxleGroups]);
    }
    return new AxleSummary([...trailerAxleGroups]);
  }

  private static toStringOrUndefined(input: unknown): string | undefined {
    if (typeof input === 'string') {
      return input;
    }
    return undefined;
  }

  private static assembleGroupSummary(
    vehicleId: string | undefined,
    vehicleAxleCount: number | undefined,
    persistedVehicleAxlesList: VehicleAxlesList,
    groupName: string,
    groupType: VehicleType,
  ): AxleSummaryGroup | undefined {
    if (vehicleId === undefined) {
      return undefined;
    }
    if (vehicleAxleCount === undefined) {
      return {
        name: groupName,
        groupType,
        vehicleId,
        axles: [],
      };
    }

    const persistedAxles =
      groupType === 'TRACTOR'
        ? persistedVehicleAxlesList.findTractorAxles(vehicleId)
        : persistedVehicleAxlesList.findTrailerAxles(vehicleId);

    if (persistedAxles === undefined || persistedAxles?.length === 0) {
      return {
        name: groupName,
        groupType,
        vehicleId,
        axles: generateNEmptyAxles(vehicleAxleCount),
      };
    }

    if (persistedAxles.length < vehicleAxleCount) {
      return {
        name: groupName,
        groupType,
        vehicleId,
        axles: [
          ...persistedAxles,
          ...generateNEmptyAxles(vehicleAxleCount - persistedAxles.length),
        ],
      };
    }

    return {
      name: groupName,
      groupType,
      vehicleId,
      axles: persistedAxles.slice(0, vehicleAxleCount),
    };
  }

  totalAxles(): number {
    return this.totalCount;
  }

  axlesCountUpTo(groupIndex: number): number {
    if (groupIndex - 1 >= this.axleGroup.length) {
      return this.totalCount;
    }

    return this.axleGroup
      .slice(0, groupIndex)
      .reduce(
        (previousValue, currentValue) =>
          previousValue + currentValue.axles.length,
        0,
      );
  }

  trailersId(): string[] {
    return this.axleGroup.reduce((previousValue, currentValue) => {
      if (currentValue.groupType === 'TRAILER') {
        previousValue.push(currentValue.vehicleId);
        return previousValue;
      }
      return previousValue;
    }, [] as string[]);
  }

  listAxles(): AxleListing[] {
    return this.axleGroup.reduce((previousValue, currentValue) => {
      return previousValue.concat(currentValue.axles);
    }, [] as AxleListing[]);
  }

  listTractorAxles(): AxleListing[] {
    return (
      this.axleGroup.find((group) => group.groupType === 'TRACTOR')?.axles ?? []
    );
  }
}

export const formatAxleGroupName = (
  axleGroup: AxleSummaryGroup,
  t: TFunction,
): string => {
  return axleGroup.groupType === 'TRACTOR'
    ? t(axleGroup.groupType.toLowerCase())
    : `${t(axleGroup.groupType.toLowerCase())} ${axleGroup.name}`;
};
